import { useState, useEffect } from "react";
import i18next from "i18next";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { etiquetaCultivo } from "@/lib/crops/label";

interface ActiveCycleInfo {
  id: string;
  crop_name: string;
  /** Código SIEX del ciclo (F4 cultivos SIEX). */
  crop_code?: number | null;
  variety_name: string | null;
  display_name: string | null;
}

/**
 * Hook to fetch active crop cycles for multiple blocks
 * Returns a Map<blockId, ActiveCycleInfo>
 */
export function useBlocksActiveCycles(
  blockIds: string[],
  companyId: string | undefined,
  referenceDate?: string
) {
  const [cyclesMap, setCyclesMap] = useState<Map<string, ActiveCycleInfo>>(new Map());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCycles = async () => {
      if (!companyId || blockIds.length === 0) {
        setCyclesMap(new Map());
        return;
      }

      setLoading(true);
      try {
        const dateToUse = referenceDate || format(new Date(), "yyyy-MM-dd");
        
        const { data, error } = await supabase
          .from("crop_cycles")
          .select("id, block_id, crop_name, crop_code, variety_name, display_name, start_date, end_date")
          .eq("company_id", companyId)
          .in("block_id", blockIds)
          .lte("start_date", dateToUse)
          .or(`end_date.is.null,end_date.gte.${dateToUse}`)
          .order("start_date", { ascending: false });

        if (error) {
          console.error("Error fetching block active cycles:", error);
          setCyclesMap(new Map());
          return;
        }

        const map = new Map<string, ActiveCycleInfo>();
        (data || []).forEach((cycle) => {
          // Only add if not already present (first match wins for overlapping)
          if (!map.has(cycle.block_id)) {
            map.set(cycle.block_id, {
              id: cycle.id,
              crop_name: cycle.crop_name,
              crop_code: (cycle as { crop_code?: number | null }).crop_code ?? null,
              variety_name: cycle.variety_name,
              display_name: cycle.display_name,
            });
          }
        });

        setCyclesMap(map);
      } catch (err) {
        console.error("Error in useBlocksActiveCycles:", err);
        setCyclesMap(new Map());
      } finally {
        setLoading(false);
      }
    };

    fetchCycles();
  }, [blockIds.join(","), companyId, referenceDate]);

  return { cyclesMap, loading };
}

/**
 * Helper to format cycle display name.
 *
 * F4 cultivos SIEX: el cultivo sale en el idioma de la app (`etiquetaCultivo`:
 * el código del ciclo y, sin él, el nombre si es seguro; «Pepino» → «Cucumber»).
 * Se llama desde fuera de React (props de selectores), así que usa la instancia
 * global de i18next; sin idioma cargado (tests), en castellano o tal cual.
 */
export function formatCycleLabel(cycle: ActiveCycleInfo): string {
  if (cycle.display_name) return cycle.display_name;
  const crop = etiquetaCultivo(cycle.crop_code ?? null, cycle.crop_name, i18next.isInitialized ? i18next.t.bind(i18next) : null, i18next.language);
  if (cycle.variety_name) return `${crop} (${cycle.variety_name})`;
  return crop;
}
